'use client';

import { CustomButton } from '@/components/common';
import { useAuth } from '@/hooks/useAuth';
import { canDeleteRoadmap, type GenericRoadmapWithProduct } from '@/utils/roadmapPermission';
import { LoadingButton } from '@/components/common/LoadingButton';
import { Button } from '@andritzot/metris-web-ui/inputs/button';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { Dialog } from 'primereact/dialog';
import { useCallback, useMemo, useState } from 'react';

import { useRoadmapActions } from '../../hooks';

interface DeleteRoadmapButtonProps {
  roadmap: GenericRoadmapWithProduct;
  onDeleteCallback?: () => void;
}

const DeleteRoadmapButton = ({ roadmap, onDeleteCallback }: DeleteRoadmapButtonProps) => {
  const { deleteRoadmap, isDeleting } = useRoadmapActions();
  const [showDeleteItem, setShowDeleteItem] = useState(false);
  const { user } = useAuth();
  const disabled = !canDeleteRoadmap(user, roadmap) || roadmap.locked;

  const confirmDeleteItem = () => {
    setShowDeleteItem(true);
  };
  const onHide = () => {
    setShowDeleteItem(false);
  };

  const handleDeleteRoadmap = useCallback(async () => {
    await deleteRoadmap(roadmap.idRoadmap);
    onHide();
    onDeleteCallback?.();
  }, [deleteRoadmap, onDeleteCallback, roadmap.idRoadmap]);

  const deleteItemDialogFooter = useMemo(
    () => (
      <>
        <Button startIcon={<CloseOutlined />} variant="outlined" onClick={onHide}>
          No
        </Button>
        <LoadingButton
          loading={isDeleting}
          icon={<CheckOutlined />}
          color="error"
          onClick={handleDeleteRoadmap}
        >
          Yes
        </LoadingButton>
      </>
    ),
    [handleDeleteRoadmap, isDeleting],
  );

  return (
    <>
      <CustomButton.Root
        startIcon={<DeleteOutlined />}
        disabled={disabled}
        color="warning"
        sx={{ borderRadius: '50px' }}
        onClick={confirmDeleteItem}
      >
        <CustomButton.Tooltip disabled={!disabled} mouseTrack>
          <div>
            <p>Deleting requires one the following permissions:</p>
            <ul>
              <li>
                <b>Admin</b>
              </li>
              <li>
                <b>Technical Product Owner</b>
              </li>
              <li>
                <b>Global Product manager</b>
              </li>
              <li>
                <b>Head of Technology</b>
              </li>
            </ul>
          </div>
        </CustomButton.Tooltip>
      </CustomButton.Root>
      <Dialog
        visible={showDeleteItem}
        style={{ width: '450px' }}
        header="Confirm delete"
        modal
        footer={deleteItemDialogFooter}
        onHide={onHide}
      >
        <div className="confirmation-content">
          <i className="pi pi-exclamation-triangle p-mr-3" style={{ fontSize: '2rem' }} />
          <span>
            Are you sure you want to delete &ldquo;<b>{roadmap.item}</b>&rdquo;?
          </span>
        </div>
      </Dialog>
    </>
  );
};

export default DeleteRoadmapButton;
